import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Checkbox, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { INotificationPreferencesViewModel } from 'viewModels';
import { useQuery } from 'react-query';
import { INotificationPreferencesUpdateInputModel } from 'inputModels';
import { ReactComponent as IconDesktop } from '../../assets/icons/iconDesktop.svg';
import { ReactComponent as IconEmail } from '../../assets/icons/iconEmail.svg';
import { ReactComponent as IconWhatsApp } from '../../assets/icons/iconWhatsapp.svg';
import { Button, NavHeader } from '../../components';
import { ModalInputCode as Input } from '../../components/Modals/ModalInputCode';
import { MenuOptionLeftUser } from '../../components/MenuOptionLeft/User';
import { SendVerificationModal } from '../../components/Modals/ModalSendEmail';
import { useApp, useAuth } from '../../context/index';
import { sendCodeEmail } from '../../controller/EmailController';
import {
  EnumDictionary,
  labelExternalContacts,
  labelNotification,
  NotificationType,
  Receiver,
  TutorialType,
  TypeContact,
} from '../../types/enums';
import { unMask } from '../../utils/removeMask';
import { validateEmail } from '../../utils/validateEmail';
import { Container, Content, ButtonContainer } from './styles';
import { UserController } from '../../controller/UserController';
import { useAlert } from '../../hooks/useAlert';
import { decryptCode } from '../../utils/crypto';
import { MASK_PHONE } from '../../constants';
import { useConfig } from '../../context/configCustom';
import TitlePage from '../../components/TitlePage';
import JoyrideTour from '../../components/JoyrideTour';

export function UserNotification() {
  const { user, tokenIsValid } = useAuth();
  const app = useApp();
  const alert = useAlert();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { pathResourceEmail } = useConfig();
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [isOpenSendVerificationModal, setIsOpenSendVerificationModal] = useState(false);
  const [expectedCode, setExpectedCode] = useState('');
  const [contactType, setContactType] = useState<TypeContact>(TypeContact.EMAIL);
  const [runningTutorial, setRunningTutorial] = useState(false);
  const [preferencesNotification, setPreferencesNotification] =
    useState<INotificationPreferencesViewModel>({
      preferences: {
        [NotificationType.ALERT]: [],
        [NotificationType.NEW_MESSAGE]: [],
        //[NotificationType.NEW_SERVICE]: [],
        [NotificationType.SENDED_NEW_DEMAND]: [],
        [NotificationType.NEW_SOLICITATION]: [],
        [NotificationType.NEW_DEMAND]: [],
        // [NotificationType.NEW_ATTACHMENT]: [],
        // [NotificationType.NEW_DATELIMIT]: [],
        // [NotificationType.STATUS_CHANGE]: [],
        // [NotificationType.PAYMENT_ALERT]: [],
        [NotificationType.DEMAND_COUNTER_PROPOSAL]: [],
        [NotificationType.SOLICITATION_CONCLUSION]: [],
        [NotificationType.DEMAND_CANCEL]: [],
        [NotificationType.REPORT_READY]: [],
      },
      contactEmail: {
        isVerified: false,
        value: '',
      },
      whatsapp: {
        isVerified: false,
        value: '',
      },
    });
  const defaultValues = {
    email: '',
    whatsapp: '',
  };
  const steps = [
    {
      target: '#start-item',
      content:
        'Nesta página, você pode escolher quais notificações deseja receber e cadastrar seus contatos para receber notificações.',
      disableBeacon: true,
    },
    {
      target: '#whatsapp-item',
      content:
        'Digite seu número de WhatsApp e conclua a validação para ativar o gerenciamento de suas notificações.',
    },
    {
      target: '#email-item',
      content:
        'Digite seu e-mail e conclua a validação para ativar o gerenciamento de suas notificações.',
    },
    {
      target: '#manage-notifications',
      content:
        'Após concluir a configuração, você pode gerenciar suas preferências de notificações.',
    },
    {
      target: '#save-item',
      content: 'Após configurar suas preferências, clique em "Salvar".',
    },
  ];

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
    setError,
  } = useForm({
    defaultValues,
    reValidateMode: 'onSubmit',
  });

  const data = watch();

  const [selectAll, setSelectAll] = useState<{ [receiver in Receiver]: boolean }>({
    [Receiver.EMAIL]: false,
    [Receiver.HUB]: false,
    [Receiver.WHATSAPP]: false,
  });

  const checkIfAllSelected = (receiver: Receiver): boolean => {
    return Object.values(preferencesNotification.preferences).every((receivers) =>
      receivers.includes(receiver),
    );
  };

  useEffect(() => {
    setSelectAll({
      [Receiver.EMAIL]: checkIfAllSelected(Receiver.EMAIL),
      [Receiver.HUB]: checkIfAllSelected(Receiver.HUB),
      [Receiver.WHATSAPP]: checkIfAllSelected(Receiver.WHATSAPP),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencesNotification]);

  const verifyFieldEmail = () => {
    if (!pathResourceEmail) {
      alert.show({
        message: 'Não foi possível enviar o e-mail, tente novamente!',
        type: 'error',
      });
      return;
    }
    if (!validateEmail(data.email)) {
      setError('email', { message: 'E-mail não é válido' });
    } else {
      app.setLoading(true);
      sendCodeEmail({ type: 'activeAccount', to: data.email, pathResource: pathResourceEmail })
        .then((resCode) => {
          setContactType(TypeContact.EMAIL);
          setIsOpenSendVerificationModal(true);
          setExpectedCode(decryptCode(resCode));
        })
        .catch(() =>
          alert.show({
            message: 'Não foi possível enviar o e-mail, tente novamente!',
            type: 'error',
          }),
        )
        .finally(() => app.setLoading(false));
    }
  };

  const verifyFieldCell = () => {
    if (data.whatsapp.length !== 15) {
      setError('whatsapp', { message: 'Telefone não é válido' });
    } else {
      let unmaskedNumber = unMask(data.whatsapp);
      if (unmaskedNumber[2] === '9') {
        unmaskedNumber = unmaskedNumber.slice(0, 2) + unmaskedNumber.slice(3);
      }

      UserController.sendWhatsappConfirmation(`55${unmaskedNumber}`)
        .then((resCode) => {
          setContactType(TypeContact.WHATSAPP);
          setExpectedCode(decryptCode(resCode));
          setIsOpenSendVerificationModal(true);
        })
        .catch(() => {
          alert.show({
            message: 'Não foi possível enviar o código de verificação, tente novamente!',
            type: 'error',
          });
        });
    }
  };

  const handleConfirmationContact = () => {
    const updatedContactNotification: Partial<INotificationPreferencesViewModel> =
      contactType === TypeContact.EMAIL
        ? { contactEmail: { value: data.email, isVerified: true } }
        : { whatsapp: { value: data.whatsapp, isVerified: true } };

    setPreferencesNotification((prev) => ({ ...prev, ...updatedContactNotification }));
    setIsOpenSendVerificationModal(false);
  };

  const handleSavePreferences = () => {
    const hasPreferencesForEmail = Object.values(preferencesNotification.preferences).some((v) =>
      v.includes(Receiver.EMAIL),
    );
    if (hasPreferencesForEmail && !preferencesNotification.contactEmail?.isVerified) {
      alert.show({
        message: 'Para salvar, verifique seu e-mail ou desative as notificações de E-mail.',
        type: 'error',
      });
      return;
    }
    app.setLoading(true);
    setIsLoadingUpdate(true);

    const update: INotificationPreferencesUpdateInputModel = {
      preferences: preferencesNotification.preferences,
      contactEmail: preferencesNotification.contactEmail?.value,
      whatsapp: preferencesNotification.whatsapp?.value
        ? unMask(preferencesNotification.whatsapp.value)
        : undefined,
    };

    UserController.updatePreferenceNotification(update)
      .then(() => alert.show({ message: 'Preferências atualizadas com sucesso', type: 'success' }))
      .catch(() => alert.show({ message: 'Erro ao atualizar as prefêrencias', type: 'error' }))
      .finally(() => {
        setIsLoadingUpdate(false);
        app.setLoading(false);
      });
  };

  const handleMarkAllCheckBox = (
    event: React.ChangeEvent<HTMLInputElement>,
    receiver: Receiver,
  ) => {
    setPreferencesNotification((prevPreferences) => {
      const updatedPreferencesNotification = { ...prevPreferences };
      Object.keys(updatedPreferencesNotification.preferences).forEach((notificationType) => {
        const receivers =
          updatedPreferencesNotification.preferences[
          notificationType as unknown as NotificationType
          ] ?? [];

        if (event.target.checked) {
          if (!receivers.includes(receiver)) {
            updatedPreferencesNotification.preferences[
              notificationType as unknown as NotificationType
            ] = [...receivers, receiver];
          }
        } else {
          updatedPreferencesNotification.preferences[
            notificationType as unknown as NotificationType
          ] = receivers.filter((r) => r !== receiver);
        }
      });
      return updatedPreferencesNotification;
    });
  };

  const { isLoading } = useQuery(
    'notificationPreferences',
    () => UserController.getNotificationPreferenceByUserId(user.id),
    {
      onSuccess: (response) => {
        const types = Object.keys(NotificationType).filter((k) => !isNaN(Number(k)));
        const preferences: EnumDictionary<NotificationType, Receiver[]> = types.reduce(
          (obj, type) => {
            return { ...obj, [type as unknown as NotificationType]: [] };
          },
          {} as EnumDictionary<NotificationType, Receiver[]>,
        );

        setPreferencesNotification({
          ...response,
          preferences: {
            ...preferences,
            ...Object.entries(response.preferences).reduce(
              (acc, [key, values]) => ({
                ...acc,
                [NotificationType[key as keyof typeof NotificationType]]: values,
              }),
              {},
            ),
          },
          contactEmail: { value: response.contactEmail || '', isVerified: !!response.contactEmail },
          whatsapp: { value: response.whatsapp || '', isVerified: !!response.whatsapp },
        });

        setValue('email', response.contactEmail || '');
        setValue('whatsapp', response.whatsapp || '');
      },
      onError: () =>
        alert.show({ message: 'Erro ao carregar preferências de notificação', type: 'error' }),
      refetchOnWindowFocus: false,
      enabled: !!user.id && tokenIsValid,
    },
  );

  useEffect(() => {
    if (isLoadingUpdate) return;
    app.setLoading(isLoading);
  }, [app, isLoading, isLoadingUpdate]);

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.USER_NOTIFICATION)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);
  return (
    <>
      <NavHeader typeHeader="company" />
      <Container>
        <MenuOptionLeftUser />
        <JoyrideTour
          type={TutorialType.USER_NOTIFICATION}
          steps={steps}
          run={runningTutorial}
          onCallbackFinished={() => setRunningTutorial(false)}
        />
        <Content>
          <div className="tableContent">
            <table>
              <thead>
                <tr>
                  <td>
                    <TitlePage
                      id="start-item"
                      title="Marque as notificações que deseja receber."
                      onRequestOpen={() => setRunningTutorial(true)}
                    />
                  </td>
                  <td className="opacity">
                    <div className="icons">
                      <IconDesktop />
                      <p>Hub</p>
                    </div>
                    <Checkbox
                      name="hub"
                      color="primary"
                      checked={selectAll[Receiver.HUB]}
                      onChange={(e) => handleMarkAllCheckBox(e, Receiver.HUB)}
                    />
                  </td>
                  <td className="alterOpcaity">
                    <div className="icons">
                      <IconWhatsApp />
                      <p>WhatsApp</p>
                    </div>
                    <Checkbox
                      name="whatsapp"
                      color="primary"
                      checked={selectAll[Receiver.WHATSAPP]}
                      onChange={(e) => handleMarkAllCheckBox(e, Receiver.WHATSAPP)}
                    />
                  </td>
                  <td className="opacity">
                    <div className="icons">
                      <IconEmail />
                      <p>E-mail</p>
                    </div>
                    <Checkbox
                      name="email"
                      color="primary"
                      checked={selectAll[Receiver.EMAIL]}
                      onChange={(e) => handleMarkAllCheckBox(e, Receiver.EMAIL)}
                    />
                  </td>
                </tr>
              </thead>
              <tbody id="manage-notifications">
                {Object.keys(NotificationType)
                  .filter((k) => !isNaN(Number(k)) && Number(k) !== Number(NotificationType.ALERT))
                  .map((type) => (
                    <tr key={type}>
                      <td className="textTable">
                        {labelNotification[type as keyof typeof NotificationType]}
                      </td>
                      {Object.keys(Receiver)
                        .filter((v) => !isNaN(Number(v)))
                        .map((receiver, idx) => (
                          <td
                            key={receiver}
                            className={`${idx % 2 ? 'dataBorder' : 'dataOpacity'}`}
                            tabIndex={0}>
                            <Checkbox
                              color="secondary"
                              checked={
                                !!preferencesNotification.preferences[
                                  type as unknown as NotificationType
                                ]?.includes(Number(receiver))
                              }
                              onChange={() => {
                                setPreferencesNotification((prev) => ({
                                  ...prev,
                                  preferences: {
                                    ...prev.preferences,
                                    [type as unknown as NotificationType]: prev.preferences[
                                      type as unknown as NotificationType
                                    ]?.includes(Number(receiver))
                                      ? prev.preferences[
                                        type as unknown as NotificationType
                                      ].filter((r) => r !== Number(receiver))
                                      : [
                                        ...(prev.preferences[
                                          type as unknown as NotificationType
                                        ] || []),
                                        Number(receiver),
                                      ],
                                  },
                                }));
                              }}
                            />
                          </td>
                        ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <h2>Cadastros</h2>
          <div className="contacts">
            <div className="contact">
              <p>WhatsApp</p>
              <div className="inputContainer">
                <Input
                  name="whatsapp"
                  className="whatsappInput"
                  id="whatsapp-item"
                  placeholder="(xx) xxxxx-xxxx"
                  control={control}
                  mask={MASK_PHONE}
                  width={isSmallScreen ? '20rem' : '30rem'}
                  errorText={errors.whatsapp?.message}
                  renderErrorOnRow
                  onChange={() => {
                    setPreferencesNotification((prev) => {
                      const updatedContactNotification = { ...prev };
                      updatedContactNotification.whatsapp = {
                        value: '',
                        isVerified: false,
                      };
                      return updatedContactNotification;
                    });
                  }}
                  endAdornment={
                    <Button
                      type="button"
                      text="Verificar"
                      background="tertiary_500"
                      color="background_white"
                      dimensions={{ width: '10rem', height: '2rem' }}
                      onClick={handleSubmit(verifyFieldCell)}
                      disabled={preferencesNotification.whatsapp?.isVerified}
                    />
                  }
                />
                <CheckCircleIcon
                  className={
                    preferencesNotification?.whatsapp?.isVerified
                      ? 'iconVerifiedField'
                      : 'iconNotVerifiedField'
                  }
                />
              </div>
            </div>
            <div className="contact">
              <p>E-mail</p>
              <div className="inputContainer">
                <Input
                  name="email"
                  id="email-item"
                  control={control}
                  errorText={errors.email?.message}
                  renderErrorOnRow
                  width={isSmallScreen ? '20rem' : '30rem'}
                  onChange={() => {
                    setPreferencesNotification((prev) => {
                      const updatedContactNotification = { ...prev };
                      updatedContactNotification.contactEmail = {
                        value: '',
                        isVerified: false,
                      };
                      return updatedContactNotification;
                    });
                  }}
                  endAdornment={
                    <Button
                      type="button"
                      text="Verificar"
                      background="tertiary_500"
                      color="background_white"
                      dimensions={{ width: '10rem', height: '2rem' }}
                      disabled={preferencesNotification?.contactEmail?.isVerified}
                      onClick={handleSubmit(verifyFieldEmail)}
                    />
                  }
                />
                <CheckCircleIcon
                  className={
                    preferencesNotification?.contactEmail?.isVerified
                      ? 'iconVerifiedField'
                      : 'iconNotVerifiedField'
                  }
                />
              </div>
            </div>
          </div>
          <ButtonContainer>
            <Button
              type="button"
              loading={isLoadingUpdate || isLoading}
              disabled={isLoadingUpdate || isLoading}
              background="primary"
              className="saveButton"
              id="save-item"
              onClick={handleSavePreferences}
              text="Salvar"
            />
          </ButtonContainer>
        </Content>
      </Container>
      {isOpenSendVerificationModal && (
        <SendVerificationModal
          isOpen={isOpenSendVerificationModal}
          onRequestClose={() => setIsOpenSendVerificationModal(false)}
          onCodeChange={setExpectedCode}
          data={{
            codeExpected: expectedCode,
            contactValue: contactType === TypeContact.EMAIL ? data.email : data.whatsapp,
            contactLabel: labelExternalContacts[contactType],
          }}
          onCallback={handleConfirmationContact}
        />
      )}
    </>
  );
}
